// OFQuillEditor.tsx
import { Alert, Grid, Stack, Theme, useTheme } from '@mui/material';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { MessageUsersTemplate } from '../../hooks/useMessageUsersTextTemplates';
import useSextforceBigBrotherRestrictedWords from '../../hooks/useSextforceBigBrotherRestrictedWords';
import useSubscriber from '../../hooks/useSubscriber';
import { isSextforceActive, stripHtml } from '../../utils/common';
import { getRestrictedWordsInMessage } from '../../utils/messageUsers';
import MessageUsersFormEditorTemplateSave from '../services/messageUsers/sendMessage/MessageUsersFormEditorTemplateSave';
import EmojiSelector from './emoji/EmojiSelector';
import { DEFAULT_EMOJI_CATEGORY, EMOJI_RECENTS_MAX, EMOJI_RECENTS_STORAGE_KEY, FULL_EMOJI_CATEGORY_MAP } from './emoji/emojiData';

/** ---- Quill toolbar & formats (ReactQuill built-in toolbar) ----
 *  NOTE: use LOWERCASE hex so Quill's active color matches the picker value visually.
 */
const LOWER_HEX_COLORS = ['#8a96a3', '#00aff0', '#1b98e0', '#000000'] as const;

const quillFormats = ['size', 'bold', 'italic', 'color', 'emoji'];

/** ---- OnlyFans color tokens ↔ hex ---- */
const OF_COLOR_MAP = {
    gray: '#8A96A3',
    'blue-1': '#00AFF0', // rgb(0, 175, 240)
    'blue-2': '#1B98E0',
    black: '#000000',
} as const;
type OfColorToken = keyof typeof OF_COLOR_MAP;

/* ======================= Converters ======================= */

/** Normalize CSS color (#hex/rgb/name) -> #rrggbb (lowercase) */
function cssColorToHex(css: string): string | null {
    if (!css) return null;

    if (typeof window === 'undefined' || typeof document === 'undefined') return css.toLowerCase();

    const el = document.createElement('div');

    el.style.color = css;

    document.body.appendChild(el);

    const rgb = getComputedStyle(el).color; // "rgb(r,g,b)"

    document.body.removeChild(el);

    const m = rgb.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/i);

    if (!m) return css.toLowerCase();

    const [r, g, b] = m.slice(1).map(Number);

    return ('#' + [r, g, b].map(n => n.toString(16).padStart(2, '0')).join('')).toLowerCase();
}

/** Returns true if the CSS color resolves to white or near-white (R,G,B all >= 240) */
function isWhiteColor(css: string): boolean {
    const hex = cssColorToHex(css);
    if (!hex) return false;
    const m = hex.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
    if (!m) return false;
    const [r, g, b] = m.slice(1).map(s => parseInt(s, 16));
    return r >= 240 && g >= 240 && b >= 240;
}

/** Utility: ensure a <span> wrapper with given classes around `el`.
 *  If parent is already a <span> with OF classes, reuse it and add classes.
 */
function ensureWrapperWithClasses(el: HTMLElement, classes: string[]): HTMLSpanElement {
    const parent = el.parentElement;
    const isOfWrapper =
        parent &&
        parent.tagName === 'SPAN' &&
        Array.from(parent.classList).some(c => c.startsWith('m-editor-fc__') || c.startsWith('m-editor-fs__'));

    if (isOfWrapper) {
        classes.forEach(c => parent!.classList.add(c));
        return parent as HTMLSpanElement;
    }

    // If the element is already a <span> without OF classes, we can reuse it
    // only if we don't need to lift outside other formatting. To keep structure
    // consistent, we prefer an outer wrapper span.
    const wrapper = document.createElement('span');

    classes.forEach(c => wrapper.classList.add(c));
    el.replaceWith(wrapper);
    wrapper.appendChild(el);

    return wrapper;
}

/** Quill -> OF: inline style colors -> wrapper <span class="m-editor-fc__*">…</span> */
function convertInlineColorToOFClasses(html: string): string {
    if (!html || typeof document === 'undefined') return html;

    const root = document.createElement('div');

    root.innerHTML = html;

    root.querySelectorAll<HTMLElement>('*').forEach(el => {
        const inlineColor = el.style?.color;

        if (!inlineColor) return;

        const hex = cssColorToHex(inlineColor);

        if (!hex) return;

        const token = (Object.keys(OF_COLOR_MAP) as OfColorToken[]).find(t => OF_COLOR_MAP[t].toLowerCase() === hex);

        if (!token) return;

        // Wrap the element so classes live on a <span> outside bold/italic, etc.
        ensureWrapperWithClasses(el, [`m-editor-fc__${token}`]);

        // Strip inline color from the inner element
        el.style.removeProperty('color');

        if (!el.getAttribute('style')) el.removeAttribute('style');
    });

    return root.innerHTML;
}

/** Quill -> OF: ql-size-* -> wrapper <span class="m-editor-fs__*">…</span> */
function convertQuillSizeToOFClasses(html: string): string {
    if (!html || typeof document === 'undefined') return html;

    const root = document.createElement('div');

    root.innerHTML = html;

    root.querySelectorAll<HTMLElement>('.ql-size-small, .ql-size-large, .ql-size-huge').forEach(el => {
        const classesToAdd: string[] = [];

        if (el.classList.contains('ql-size-small')) classesToAdd.push('m-editor-fs__sm');
        if (el.classList.contains('ql-size-large')) classesToAdd.push('m-editor-fs__l');
        if (el.classList.contains('ql-size-huge')) classesToAdd.push('m-editor-fs__lg');

        if (classesToAdd.length) {
            ensureWrapperWithClasses(el, classesToAdd);
            // Remove Quill size classes from the inner element
            el.classList.remove('ql-size-small', 'ql-size-large', 'ql-size-huge');
            if (!el.className) el.removeAttribute('class');
        }
    });

    return root.innerHTML;
}

/** Compose: Quill HTML -> OF-class HTML (order doesn't matter; wrappers merge) */
function toOFHtml(html: string): string {
    // Run color and size conversions; they share the same wrapper when both apply
    const withColors = convertInlineColorToOFClasses(html);

    return convertQuillSizeToOFClasses(withColors);
}

/** OF -> Quill: m-editor-fc__* -> inline color; m-editor-fs__* -> ql-size-* */
function convertOFToQuill(html: string): string {
    if (!html || typeof document === 'undefined') return html;

    const root = document.createElement('div');

    root.innerHTML = html;

    // Colors
    root.querySelectorAll<HTMLElement>('[class*="m-editor-fc__"]').forEach(el => {
        const cls = Array.from(el.classList).find(c => c.startsWith('m-editor-fc__'));
        if (!cls) return;
        const token = cls.replace('m-editor-fc__', '') as OfColorToken;
        const hex = OF_COLOR_MAP[token];
        if (hex && !el.style.color) el.style.color = hex;
        el.classList.remove(cls);
    });

    // Sizes
    root.querySelectorAll<HTMLElement>('[class*="m-editor-fs__"]').forEach(el => {
        const cls = Array.from(el.classList).find(c => c.startsWith('m-editor-fs__'));
        if (!cls) return;
        const token = cls.replace('m-editor-fs__', '');
        if (token === 'sm') el.classList.add('ql-size-small');
        if (token === 'l') el.classList.add('ql-size-large');
        if (token === 'lg') el.classList.add('ql-size-huge');
        el.classList.remove(cls);
    });

    return root.innerHTML;
}

/* ======================= Component ======================= */

type Props = {
    /** Single source of truth: OF-class HTML (e.g., v.text) */
    valueOF: string;
    /** We emit OF-class HTML only */
    onChangeOF: (ofHtml: string) => void;
    placeholder?: string;
    readOnly?: boolean;
    className?: string;
    showUsernameButton?: boolean;
    showSaveTemplate?: boolean;
};

/**
 * Loop-proof & cursor-safe editor with a custom "%USERNAME%" button:
 *   - accepts OF HTML (valueOF)
 *   - shows Quill HTML internally
 *   - emits OF HTML on edits
 *   - IGNORES prop updates that came from *our own* last emit (prevents resets)
 *   - toolbar includes a custom button that inserts "%USERNAME%" at the caret
 */
const OnlyFansQuillMessageEditor = ({
    valueOF,
    onChangeOF,
    placeholder,
    readOnly,
    className,
    showUsernameButton,
    showSaveTemplate = true,
}: Props) => {
    const theme: Theme = useTheme();

    const [messageHasRestrictedWords, setMessageHasRestrictedWords] = useState<boolean>(false);
    const [messageFoundRestrictedWords, setMessageFoundRestrictedWords] = useState<string[]>([]);
    const { data: restrictedWords, isLoading: isLoadingRestrictedWords } = useSextforceBigBrotherRestrictedWords();
    const { data: subscriber } = useSubscriber();
    const [saveTemplate, setSaveTemplate] = useState<boolean>(false);
    const [saveTemplateName, setSaveTemplateName] = useState<string>('');

    const [externallyOpenLoadTemplateDialog, setExternallyOpenLoadTemplateDialog] = useState<boolean>(false);

    const handleLoadTemplate = (template: MessageUsersTemplate) => {
        onChangeOF(template.message);
    };

    // Check the message doesn't contain any OnlyFans restricted words
    const checkForRestrictedWords = useCallback(
        (text: string) => {
            if (!text || isLoadingRestrictedWords || !restrictedWords || !subscriber) {
                setMessageHasRestrictedWords(false);
                setMessageFoundRestrictedWords([]);
                return;
            }

            const restrictedWordsFound = getRestrictedWordsInMessage(subscriber, text, restrictedWords);
            setMessageHasRestrictedWords(restrictedWordsFound.length > 0);
            setMessageFoundRestrictedWords(restrictedWordsFound);
        },
        [isLoadingRestrictedWords, restrictedWords, subscriber],
    );

    const quillRef = useRef<ReactQuill | null>(null);
    const lastSelectionRef = useRef<{ index: number; length: number } | null>(null);

    // Track if we're currently debouncing
    const isDebouncingRef = useRef(false);

    // Quill HTML shown in the editor (internal state)
    const [quillHtml, setQuillHtml] = useState<string>(() =>
        typeof window === 'undefined' ? valueOF ?? '' : convertOFToQuill(valueOF ?? ''),
    );
    const [recentEmojis, setRecentEmojis] = useState<string[]>([]);
    const [selectedEmojiCategory, setSelectedEmojiCategory] = useState<string>(DEFAULT_EMOJI_CATEGORY);

    // Guards to prevent feedback loops + selection resets
    const applyingIncoming = useRef(false);
    const lastEmittedOF = useRef<string>(valueOF ?? '');

    // Apply *external* prop changes only (skip if it matches what we just emitted)
    useEffect(() => {
        if (valueOF === lastEmittedOF.current) {
            // This update originated from our own typing; do not touch the editor.
            return;
        }

        const incomingQuill = convertOFToQuill(valueOF ?? '');
        if (incomingQuill !== quillHtml) {
            applyingIncoming.current = true; // next onChange is synthetic

            setQuillHtml(incomingQuill);
        }
    }, [valueOF]);

    // DEBOUNCED expensive operations (OF conversion + restricted words check)
    const debouncedProcessHtml = useMemo(
        () =>
            debounce((html: string) => {
                const ofHtml = toOFHtml(html);

                if (ofHtml !== lastEmittedOF.current) {
                    lastEmittedOF.current = ofHtml;
                    checkForRestrictedWords(stripHtml(ofHtml));
                    onChangeOF(ofHtml);
                }

                isDebouncingRef.current = false;
            }, 300), // 300ms delay - adjust as needed
        [onChangeOF, checkForRestrictedWords],
    );

    // Cleanup debounce on unmount
    useEffect(() => {
        return () => {
            debouncedProcessHtml.cancel();
        };
    }, [debouncedProcessHtml]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        try {
            const saved = window.localStorage.getItem(EMOJI_RECENTS_STORAGE_KEY);
            if (!saved) return;
            const parsed = JSON.parse(saved);
            if (!Array.isArray(parsed)) return;
            setRecentEmojis(parsed.filter(value => typeof value === 'string').slice(0, EMOJI_RECENTS_MAX));
        } catch {
            // ignore malformed storage values
        }
    }, []);

    const persistRecentEmojis = useCallback((nextRecents: string[]) => {
        if (typeof window === 'undefined') return;
        window.localStorage.setItem(EMOJI_RECENTS_STORAGE_KEY, JSON.stringify(nextRecents));
    }, []);

    const addRecentEmoji = useCallback(
        (emoji: string) => {
            setRecentEmojis(prev => {
                const nextRecents = [emoji, ...prev.filter(item => item !== emoji)].slice(0, EMOJI_RECENTS_MAX);
                persistRecentEmojis(nextRecents);
                return nextRecents;
            });
        },
        [persistRecentEmojis],
    );

    const handleChange = (html: string) => {
        if (applyingIncoming.current) {
            applyingIncoming.current = false;
            return;
        }

        // IMMEDIATE: Update editor state for responsive typing
        if (html !== quillHtml) {
            setQuillHtml(html);
        }

        // DEBOUNCED: Expensive operations only after user stops typing
        isDebouncingRef.current = true;
        debouncedProcessHtml(html);
    };

    const handleSelectionChange = useCallback((range: { index: number; length: number } | null) => {
        if (range) {
            lastSelectionRef.current = range;
        }
    }, []);

    const insertEmojiAtCaret = useCallback(
        (emoji: string) => {
            if (readOnly) return;
            const editor = quillRef.current?.getEditor();
            if (!editor) return;

            const currentSelection = editor.getSelection();
            const range = currentSelection ?? lastSelectionRef.current ?? { index: editor.getLength(), length: 0 };

            editor.focus();
            editor.insertText(range.index, emoji, 'user');
            editor.setSelection(range.index + emoji.length, 0, 'user');
            lastSelectionRef.current = { index: range.index + emoji.length, length: 0 };
            addRecentEmoji(emoji);
        },
        [addRecentEmoji, readOnly],
    );

    const allEmojiCategories = useMemo(
        () =>
            [
                ...(recentEmojis.length > 0 ? [{ label: 'Recent', emojis: recentEmojis }] : []),
                ...FULL_EMOJI_CATEGORY_MAP.map(category => ({ label: category.label, emojis: category.emojis })),
            ] as Array<{ label: string; emojis: string[] }>,
        [recentEmojis],
    );

    useEffect(() => {
        const availableLabels = new Set(allEmojiCategories.map(category => category.label));
        if (availableLabels.has(selectedEmojiCategory)) return;
        setSelectedEmojiCategory(
            availableLabels.has(DEFAULT_EMOJI_CATEGORY) ? DEFAULT_EMOJI_CATEGORY : allEmojiCategories[0]?.label ?? 'Recent',
        );
    }, [allEmojiCategories, selectedEmojiCategory]);

    // Modules with ONE toolbar (array form) + custom handler
    const modules = useMemo(
        () => ({
            clipboard: {
                matchers: [
                    ['*', (_node: any, delta: any) => {
                        delta.ops = (delta.ops as any[]).map(op => {
                            if (op.attributes?.color && isWhiteColor(op.attributes.color)) {
                                const { color: _removed, ...restAttrs } = op.attributes;
                                const newOp = { ...op, attributes: restAttrs };
                                if (Object.keys(restAttrs).length === 0) delete newOp.attributes;
                                return newOp;
                            }
                            return op;
                        });
                        return delta;
                    }],
                ],
            },
            toolbar: {
                container: [
                    [{ size: [] }],
                    ['bold', 'italic'],
                    [{ color: [...LOWER_HEX_COLORS] }],
                    ['clean'],
                    showUsernameButton ? ['insertUsername'] : [],
                    showSaveTemplate ? ['openTemplates'] : [],
                ],
                handlers: {
                    insertUsername: function (this: any) {
                        if (readOnly) return;

                        const quill = this.quill as any;
                        const range = quill.getSelection(true);

                        if (!range) return;

                        const formats = quill.getFormat(range);
                        const text = ' %USERNAME%';

                        quill.insertText(range.index, text, formats, 'user');
                        quill.setSelection(range.index + text.length, 0, 'user');
                    },
                    openTemplates: function (this: any) {
                        if (readOnly) return;

                        setExternallyOpenLoadTemplateDialog(true);
                    },
                },
            },
        }),
        [readOnly, showUsernameButton, showSaveTemplate],
    );

    return (
        <>
            {/* Toolbar wrap + custom button label */}
            <style>{`
        /* Wrap toolbar onto multiple lines */
        .ofquill .ql-toolbar.ql-snow {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 2px 2px;
          width: 100%;
        }
        
        /* ~4 lines tall by default, but can grow */
        .ofquill .ql-container .ql-editor {
          line-height: 1.5;           /* ensure a known line-height */
          min-height: calc(1.5em * 5);/* 5 rows of content height */
          font: 16px/1.3334 Roboto,sans-serif;
        }

        .ofquill .ql-toolbar.ql-snow .ql-formats {
          display: flex;
          flex-wrap: wrap;
          margin-top: auto;
          margin-bottom: auto;
          margin-right: 2px;
        }

        /* Ensure individual controls don't collapse */
        .ofquill .ql-toolbar.ql-snow .ql-formats > * {
          margin-right: 0px;
        }

        /* --- Consistent hover effect for all toolbar buttons --- */
        .ofquill .ql-toolbar.ql-snow button:hover,
        .ofquill .ql-toolbar.ql-snow .ql-picker-label:hover {
          background: #f3f3f3;
        }

        .ofquill .ql-toolbar.ql-snow button:active,
        .ofquill .ql-toolbar.ql-snow .ql-picker-label:active {
          background: #e9e9e9;
        }

        /* --- Custom %USERNAME% button --- */
        .ofquill .ql-toolbar .ql-insertUsername {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          background: #fff;
          cursor: pointer;
          position: relative;
        }

        /* Hover/active states to match Snow theme a bit */
        .ofquill .ql-toolbar .ql-insertUsername:hover {
          background: #f3f3f3;
        }

        /* Tooltip for username button */
        .ofquill .ql-toolbar .ql-insertUsername::before {
          content: "Insert %USERNAME%";
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(0, 0, 0, 0.8);
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          white-space: nowrap;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.2s;
          margin-bottom: 5px;
          z-index: 1000;
        }

        .ofquill .ql-toolbar .ql-insertUsername:hover::before {
          opacity: 1;
        }

        /* Display the user icon using SVG */
        .ofquill .ql-toolbar .ql-insertUsername::after {
          content: "";
          display: inline-block;
          width: 18px;
          height: 18px;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23444'%3E%3Cpath d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z'/%3E%3C/svg%3E");
          background-size: contain;
          background-repeat: no-repeat;
          background-position: center;
        }

        /* --- Custom Open Templates button with FolderOpenIcon --- */
        .ofquill .ql-toolbar .ql-openTemplates {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          background: #fff;
          cursor: pointer;
          margin-left: auto;  /* Push to the right */
          position: relative;  /* For tooltip positioning */
        }

        .ofquill .ql-toolbar .ql-openTemplates:hover {
          background: #f3f3f3;
        }

        .ofquill .ql-toolbar .ql-openTemplates:active {
          background: #e9e9e9;
        }

        /* Tooltip */
        .ofquill .ql-toolbar .ql-openTemplates::before {
          content: "Load from Captions...";
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(0, 0, 0, 0.8);
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          white-space: nowrap;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.2s;
          margin-bottom: 5px;
          z-index: 1000;
        }

        .ofquill .ql-toolbar .ql-openTemplates:hover::before {
          opacity: 1;
        }

        /* Display the folder icon using SVG */
        .ofquill .ql-toolbar .ql-openTemplates::after {
          content: "";
          display: inline-block;
          width: 18px;
          height: 18px;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23444'%3E%3Cpath d='M20 6h-8l-2-2H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V8h16v10z'/%3E%3C/svg%3E");
          background-size: contain;
          background-repeat: no-repeat;
          background-position: center;
        }
      `}</style>
            {showSaveTemplate && (
                <Grid item xs={12}>
                    <MessageUsersFormEditorTemplateSave
                        subscriber={subscriber}
                        saveTemplate={saveTemplate}
                        setSaveTemplate={setSaveTemplate}
                        saveTemplateName={saveTemplateName}
                        setSaveTemplateName={setSaveTemplateName}
                        handleLoadTemplate={handleLoadTemplate}
                        disabled={false}
                        showSave={false}
                        externallyOpenLoadTemplateDialog={externallyOpenLoadTemplateDialog}
                        setExternallyOpenLoadTemplateDialog={setExternallyOpenLoadTemplateDialog}
                    />
                </Grid>
            )}
            <Stack direction={'column'} spacing={0}>
                <div className="ofquill notranslate">
                    <ReactQuill
                        ref={inst => (quillRef.current = inst)}
                        theme="snow"
                        className={className}
                        readOnly={readOnly}
                        placeholder={placeholder}
                        value={quillHtml}
                        onChange={handleChange}
                        onChangeSelection={handleSelectionChange}
                        modules={modules}
                        formats={quillFormats}
                    />
                </div>
                <EmojiSelector
                    categories={allEmojiCategories}
                    selectedCategory={selectedEmojiCategory}
                    onSelectCategory={setSelectedEmojiCategory}
                    onSelectEmoji={insertEmojiAtCaret}
                    readOnly={readOnly}
                />
                {messageHasRestrictedWords && (
                    <Alert
                        variant="filled"
                        severity={isSextforceActive(subscriber) && subscriber.sextforce?.bigBrother?.active ? 'error' : 'warning'}
                    >
                        Message contains words that are restricted by OnlyFans!
                        {messageFoundRestrictedWords.length > 0 && (
                            <>
                                <br />
                                {messageFoundRestrictedWords.join(', ')}
                            </>
                        )}
                    </Alert>
                )}
            </Stack>
        </>
    );
};

export default OnlyFansQuillMessageEditor;
